import { createFromTemplate, slot } from '../../core/dom/template.js';
import { createElement } from '../../core/dom/helpers.js';
import { buildMenuTree } from '../../core/menu/menu-tree.js';
import { createSidebar } from '../../components/sidebar/sidebar.js';
import { createContextSwitcher } from '../../components/context-switcher/context-switcher.js';
import { createThemeToggle } from '../../components/theme-toggle/theme-toggle.js';
import { createDesignToggle } from '../../components/design-toggle/design-toggle.js';
import { createInlineError } from '../../components/inline-error/inline-error.js';
import { createUserMenu } from '../../components/user-menu/user-menu.js';
import { createFoldState } from '../../core/ui/fold-state.js';
import { normalizePath } from '../../core/routing/router.js';
import tpl from './app-shell.tpl.js';

/**
 * @param {HTMLElement} el
 * @param {{
 *   apiClient: import('../../contracts/api.js').ApiClient,
 *   eventBus: import('../../contracts/page.js').PageServices['eventBus'],
 *   t: import('../../contracts/i18n.js').Translator,
 *   auth: import('../../contracts/auth.js').AuthState,
 *   access: import('../../contracts/access.js').AccessResolver,
 *   contextManager: import('../../contracts/context.js').ContextManager,
 *   pageRegistry: import('../../contracts/page.js').PageRegistry,
 *   router: import('../../contracts/router.js').Router,
 *   logger: import('../../contracts/page.js').PageServices['logger'],
 *   contextOptions: Array<{ country: string, lang: string, label: string }>,
 *   fetchMenu: (signal?: AbortSignal) => Promise<import('../../contracts/menu.js').MenuItem[]>,
 *   fetchAccess: (signal?: AbortSignal) => Promise<import('../../contracts/access.js').AccessData>,
 *   themeManager?: { get(): 'light'|'dark', set(p: 'light'|'dark'): void, subscribe(fn: (theme: 'light'|'dark') => void): () => void, destroy(): void },
 *   designManager?: { get(): string, getAvailable(): string[], set(d: string): void, subscribe(fn: (d: string) => void): () => void, destroy(): void },
 *   brandConfig?: { appTitle?: string, logoUrl?: string },
 *   uploadClient?: import('../../contracts/upload.js').UploadClient,
 *   mediaClient?: import('../../contracts/media.js').MediaClient,
 *   filePicker?: import('../../contracts/forms.js').FilePicker,
 *   formPicker?: import('../../contracts/forms.js').FormPicker,
 *   localeConfig?: import('../../contracts/locale.js').LocaleConfig,
 *   onLogout?: () => void,
 * }} deps
 */
export function createAppShell(el, deps) {
  const {
    apiClient, eventBus, t, auth, access, contextManager,
    pageRegistry, router, logger, contextOptions, fetchMenu, fetchAccess,
    themeManager, designManager, brandConfig,
  } = deps;

  const root = createFromTemplate(tpl);
  const headerSlot = slot(root, 'header');
  const menuToggleBtn = slot(root, 'menu-toggle');
  const sidebarCloseBtn = slot(root, 'sidebar-close');
  const sidebarSlot = slot(root, 'sidebar');
  const sidebarHeaderSlot = slot(root, 'sidebar-header');
  const sidebarNavSlot = slot(root, 'sidebar-nav');
  const contentSlot = slot(root, 'content');

  // Backdrop element for mobile overlay
  const backdrop = createElement('div', { class: 'app-shell-backdrop' });
  /** @type {Element} */ (root.querySelector('.app-shell-body')).appendChild(backdrop);

  el.appendChild(root);

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  let mountVersion = 0;
  let accessRefreshVersion = 0;
  let bootVersion = 0;
  let destroyed = false;
  let bootReady = false;
  /** @type {string | null} */
  let pendingRoute = null;
  let pendingAccessRefresh = false;
  /** @type {string | null} */
  let lastMountedPageKey = null;

  /** @type {{ destroy: () => void } | null} */
  let currentPage = null;
  /** @type {AbortController | null} */
  let currentPageController = null;
  /** @type {ReturnType<typeof createSidebar> | null} */
  let sidebar = null;
  /** @type {AbortController | null} */
  let currentBootController = null;
  /** @type {AbortController | null} */
  let currentAccessRefreshController = null;

  /** @type {import('../../contracts/menu.js').MenuNode[]} */
  let currentMenuTree = [];

  /** @type {Array<() => void>} */
  const unsubscribers = [];

  // ---------------------------------------------------------------------------
  // Header
  // ---------------------------------------------------------------------------
  const headerTitle = createElement('h1', { class: 'app-shell-header-title' },
    [brandConfig?.appTitle || 'Admin']);
  headerSlot.appendChild(headerTitle);

  /** @type {HTMLImageElement | null} */
  let headerLogo = null;
  if (brandConfig?.logoUrl) {
    headerLogo = /** @type {HTMLImageElement} */ (createElement('img', {
      class: 'app-shell-header-logo',
      src: brandConfig.logoUrl,
      alt: '',
    }));
    headerSlot.appendChild(headerLogo);
  }

  function syncLogoClass() {
    root.classList.toggle('app-shell--has-logo', headerLogo !== null);
  }
  syncLogoClass();

  const spacer = createElement('div', { class: 'app-shell-header-spacer' });
  headerSlot.appendChild(spacer);

  // Theme toggle (optional — only if themeManager provided)
  /** @type {{ update(t: string): void, destroy(): void } | null} */
  let themeToggle = null;
  if (themeManager) {
    themeToggle = createThemeToggle(headerSlot, {
      theme: themeManager.get(),
      onChange: (t) => themeManager.set(t),
    });
    unsubscribers.push(themeManager.subscribe(() => {
      if (themeToggle) themeToggle.update(themeManager.get());
    }));
  }

  // Design toggle (optional — only if designManager provided)
  /** @type {{ update(d: string): void, destroy(): void } | null} */
  let designToggle = null;
  if (designManager) {
    designToggle = createDesignToggle(headerSlot, {
      designs: designManager.getAvailable(),
      current: designManager.get(),
      onSelect: (d) => designManager.set(d),
    });
    unsubscribers.push(designManager.subscribe((d) => {
      if (designToggle) designToggle.update(d);
    }));
  }

  // User menu (replaces plain user label)
  /** @type {{ destroy(): void } | null} */
  let userMenu = null;
  if (auth.user) {
    userMenu = createUserMenu(headerSlot, {
      user: auth.user,
      onNavigate: (path) => {
        router.navigate(path);
        foldState.close();
      },
      onLogout: deps.onLogout || (() => {}),
    });
  }

  // Context switcher — now in sidebar header, above nav
  const ctxSwitcher = createContextSwitcher(sidebarHeaderSlot, {
    current: contextManager.get(),
    options: contextOptions,
    onChange: (ctx) => contextManager.set(ctx),
  });

  // ---------------------------------------------------------------------------
  // Fold state + mobile
  // ---------------------------------------------------------------------------
  const MOBILE_BREAKPOINT = 768;
  function checkMobile() { return window.innerWidth < MOBILE_BREAKPOINT; }

  const foldState = createFoldState({ isMobile: checkMobile });

  // Apply initial fold state on desktop
  if (foldState.isFolded() && !checkMobile()) {
    root.classList.add('app-shell--folded');
  }

  // Menu toggle wiring
  menuToggleBtn.addEventListener('click', () => {
    foldState.toggleFold();
  });

  // In-drawer close button (mobile) — the hamburger is covered by the open
  // drawer, so this gives a reachable way to dismiss it without navigating.
  sidebarCloseBtn.addEventListener('click', () => {
    foldState.close();
  });

  // Backdrop click closes mobile sidebar
  backdrop.addEventListener('click', () => {
    foldState.close();
  });

  // Fold state subscriber — sync CSS classes
  unsubscribers.push(foldState.subscribe(() => {
    if (checkMobile()) {
      root.classList.toggle('app-shell--sidebar-open', foldState.isOpen());
      // Focus management: prefer <nav> if present, fallback to sidebar container
      if (foldState.isOpen()) {
        const nav = sidebarSlot.querySelector('nav');
        (nav || sidebarSlot).focus();
      } else if (menuToggleBtn?.isConnected) {
        menuToggleBtn.focus();
      }
    } else {
      root.classList.toggle('app-shell--folded', foldState.isFolded());
    }
  }));

  // Resize handler — detect mobile ↔ desktop transition
  let wasMobile = checkMobile();
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let resizeTimer;
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      const nowMobile = checkMobile();
      if (wasMobile === nowMobile) return;

      if (wasMobile && !nowMobile) {
        // Mobile → Desktop: close overlay, apply persisted fold state
        root.classList.remove('app-shell--sidebar-open');
        foldState.close();
        root.classList.toggle('app-shell--folded', foldState.isFolded());
      } else {
        // Desktop → Mobile: remove fold class
        root.classList.remove('app-shell--folded');
      }
      wasMobile = nowMobile;
    }, 100);
  }
  window.addEventListener('resize', onResize);

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------
  showLoading(sidebarNavSlot);
  boot();

  async function boot() {
    bootVersion++;
    const thisBoot = bootVersion;
    accessRefreshVersion++;
    const thisAccessVersion = accessRefreshVersion;

    // Per-boot abort controller (separate from shell-level)
    if (currentBootController) currentBootController.abort();
    currentBootController = new AbortController();
    const bootSignal = currentBootController.signal;

    try {
      const [menuItems, accessData] = await Promise.all([
        fetchMenu(bootSignal),
        fetchAccess(bootSignal),
      ]);

      if (destroyed || thisBoot !== bootVersion) return;
      if (thisAccessVersion !== accessRefreshVersion) return;

      // Build menu tree
      currentMenuTree = buildMenuTree(menuItems, (msg, data) => {
        /** @type {any} */ (logger).warn?.(`[menu-tree] ${msg}`, data);
      });

      // Update access resolver
      access.update(accessData);
      const filteredTree = access.filterMenu(currentMenuTree);

      // Render sidebar
      sidebarNavSlot.innerHTML = '';
      sidebar = createSidebar(sidebarNavSlot, {
        menuTree: filteredTree,
        activeRoute: pendingRoute ? normalizePath('/' + pendingRoute) : undefined,
        onNavigate: (path) => {
          router.navigate(path);
          foldState.close();
        },
      });

      // If access events arrived during boot, await refresh before first mount.
      // Loop: a new event can arrive during the refresh await itself.
      while (pendingAccessRefresh) {
        pendingAccessRefresh = false;
        try {
          await doAccessRefresh();
        } catch (err) {
          /** @type {any} */ (logger).error?.('[app-shell] Pending access refresh failed, continuing with boot data', err);
          break;
        }
        if (destroyed || thisBoot !== bootVersion) return;
      }

      // Boot complete — enable route mounting
      bootReady = true;
      currentBootController = null;

      // Mount queued route
      if (pendingRoute !== null) {
        const route = pendingRoute;
        pendingRoute = null;
        mountPage(route);
      }
    } catch (err) {
      if (destroyed || thisBoot !== bootVersion) return;
      currentBootController = null;

      // Show error in sidebar with retry
      sidebarNavSlot.innerHTML = '';
      createInlineError(sidebarNavSlot, {
        title: 'Navigation failed',
        message: 'Could not load navigation. Please try again.',
        onRetry: () => {
          showLoading(sidebarNavSlot);
          boot();
        },
      });

      /** @type {any} */ (logger).error?.('[app-shell] Boot failed', err);
    }
  }

  // ---------------------------------------------------------------------------
  // Route mounting
  // ---------------------------------------------------------------------------

  /**
   * @param {string} pageKey
   */
  async function mountPage(pageKey) {
    if (destroyed) return;
    if (!bootReady) {
      pendingRoute = pageKey;
      return;
    }

    mountVersion++;
    const thisVersion = mountVersion;

    // 1. Destroy current page
    safeDestroy();

    // 2. Clear query params (shell-only: direct replaceState to clear query.
    //    Pages must use urlState — never call replaceState directly.)
    window.history.replaceState(null, '', window.location.pathname + window.location.hash);

    // 3. Look up page
    const pageDef = pageRegistry.get(pageKey);
    if (!pageDef) {
      contentSlot.innerHTML = '';
      createInlineError(contentSlot, { title: 'Page not found', message: `No page registered for "${pageKey}".` });
      lastMountedPageKey = null;
      if (sidebar) sidebar.setActiveRoute('');
      return;
    }

    // 4. Check permission
    if (pageDef.permission && !access.hasPermission(pageDef.permission)) {
      contentSlot.innerHTML = '';
      createInlineError(contentSlot, { title: 'Access denied', message: 'You do not have permission to view this page.' });
      lastMountedPageKey = null;
      if (sidebar) sidebar.setActiveRoute('');
      return;
    }

    // 5. Resolve mount function (handle lazy loader)
    /** @type {import('../../contracts/page.js').PageMountFn} */
    let mountFn;

    if ('loader' in pageDef && typeof pageDef.loader === 'function') {
      try {
        const mod = await pageDef.loader();
        if (thisVersion !== mountVersion) return; // stale

        if (typeof mod.default !== 'function') {
          throw new Error(`Page loader for "${pageKey}" did not return a default mount function`);
        }
        mountFn = mod.default;
      } catch (err) {
        if (thisVersion !== mountVersion) return;
        contentSlot.innerHTML = '';
        createInlineError(contentSlot, {
          title: 'Failed to load page',
          message: String(err),
          onRetry: () => {
            // Retry uses current route, not the originally-failed route
            const currentKey = router.currentRoute().pageKey;
            mountPage(currentKey);
          },
        });
        /** @type {any} */ (logger).error?.(`[app-shell] Loader failed for "${pageKey}"`, err);
        return;
      }
    } else {
      mountFn = /** @type {import('../../contracts/page.js').StaticPageDef} */ (pageDef).mount;
    }

    // 6. Re-check permission (can change mid-load)
    if (thisVersion !== mountVersion) return;
    if (pageDef.permission && !access.hasPermission(pageDef.permission)) {
      contentSlot.innerHTML = '';
      createInlineError(contentSlot, { title: 'Access denied', message: 'You do not have permission to view this page.' });
      lastMountedPageKey = null;
      return;
    }

    // 7. Create page-level abort controller + services
    currentPageController = new AbortController();
    /** @type {import('../../contracts/page.js').PageServices} */
    const services = {
      apiClient,
      eventBus,
      logger,
      t,
      auth,
      context: contextManager.get(),
      signal: currentPageController.signal,
      uploadClient: deps.uploadClient,
      mediaClient: deps.mediaClient,
      filePicker: deps.filePicker,
      formPicker: deps.formPicker,
      localeConfig: /** @type {import('../../contracts/locale.js').LocaleConfig} */ (deps.localeConfig),
    };

    // 8. Mount
    contentSlot.innerHTML = '';
    try {
      currentPage = mountFn(contentSlot, services);
    } catch (err) {
      contentSlot.innerHTML = '';
      createInlineError(contentSlot, {
        title: 'Page error',
        message: String(err),
        onRetry: () => {
          const currentKey = router.currentRoute().pageKey;
          mountPage(currentKey);
        },
      });
      /** @type {any} */ (logger).error?.(`[app-shell] Mount failed for "${pageKey}"`, err);
      currentPage = null;
      return;
    }

    // 9. Update sidebar + tracking
    lastMountedPageKey = pageKey;
    const activePath = normalizePath('/' + pageKey);
    if (sidebar) sidebar.setActiveRoute(activePath);
  }

  /**
   * Context change — remount WITHOUT clearing query params.
   */
  async function remountCurrentPage() {
    if (destroyed || !lastMountedPageKey) return;

    mountVersion++;
    const thisVersion = mountVersion;
    const pageKey = lastMountedPageKey;

    // Destroy current page but skip query clearing
    safeDestroy();

    const pageDef = pageRegistry.get(pageKey);
    if (!pageDef) return;

    if (pageDef.permission && !access.hasPermission(pageDef.permission)) return;

    /** @type {import('../../contracts/page.js').PageMountFn} */
    let mountFn;

    if ('loader' in pageDef && typeof pageDef.loader === 'function') {
      try {
        const mod = await pageDef.loader();
        if (thisVersion !== mountVersion) return;

        if (typeof mod.default !== 'function') {
          throw new Error(`Page loader for "${pageKey}" did not return a default mount function`);
        }
        mountFn = mod.default;
      } catch (err) {
        if (thisVersion !== mountVersion) return;
        contentSlot.innerHTML = '';
        createInlineError(contentSlot, {
          title: 'Failed to load page',
          message: String(err),
          onRetry: () => {
            const currentKey = router.currentRoute().pageKey;
            mountPage(currentKey);
          },
        });
        /** @type {any} */ (logger).error?.(`[app-shell] Remount loader failed for "${pageKey}"`, err);
        currentPage = null;
        lastMountedPageKey = null;
        return;
      }
    } else {
      mountFn = /** @type {import('../../contracts/page.js').StaticPageDef} */ (pageDef).mount;
    }

    if (thisVersion !== mountVersion) return;

    currentPageController = new AbortController();
    /** @type {import('../../contracts/page.js').PageServices} */
    const services = {
      apiClient, eventBus, logger, t, auth,
      context: contextManager.get(),
      signal: currentPageController.signal,
      uploadClient: deps.uploadClient,
      mediaClient: deps.mediaClient,
      filePicker: deps.filePicker,
      formPicker: deps.formPicker,
      localeConfig: /** @type {import('../../contracts/locale.js').LocaleConfig} */ (deps.localeConfig),
    };

    contentSlot.innerHTML = '';
    try {
      currentPage = mountFn(contentSlot, services);
    } catch (err) {
      contentSlot.innerHTML = '';
      createInlineError(contentSlot, {
        title: 'Page error',
        message: String(err),
        onRetry: () => {
          const currentKey = router.currentRoute().pageKey;
          mountPage(currentKey);
        },
      });
      /** @type {any} */ (logger).error?.(`[app-shell] Remount failed for "${pageKey}"`, err);
      currentPage = null;
      lastMountedPageKey = null;
      return;
    }

    lastMountedPageKey = pageKey;
  }

  // ---------------------------------------------------------------------------
  // Access refresh
  // ---------------------------------------------------------------------------

  /**
   * Perform a version-guarded, abortable access refresh.
   */
  async function doAccessRefresh() {
    accessRefreshVersion++;
    const thisVersion = accessRefreshVersion;

    // Abort any previous in-flight refresh
    if (currentAccessRefreshController) currentAccessRefreshController.abort();
    currentAccessRefreshController = new AbortController();
    const refreshSignal = currentAccessRefreshController.signal;

    const accessData = await fetchAccess(refreshSignal);

    if (destroyed || thisVersion !== accessRefreshVersion) return;
    currentAccessRefreshController = null;

    access.update(accessData);
    const filteredTree = access.filterMenu(currentMenuTree);

    if (sidebar) {
      const activePath = lastMountedPageKey ? normalizePath('/' + lastMountedPageKey) : undefined;
      sidebar.update(filteredTree, activePath);
    }

    // Re-check current page permission
    if (lastMountedPageKey) {
      const pageDef = pageRegistry.get(lastMountedPageKey);
      if (pageDef?.permission && !access.hasPermission(pageDef.permission)) {
        safeDestroy();
        contentSlot.innerHTML = '';
        createInlineError(contentSlot, { title: 'Access denied', message: 'You no longer have permission to view this page.' });
        lastMountedPageKey = null;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Subscriptions
  // ---------------------------------------------------------------------------

  // Router subscription — fires immediately with current route
  unsubscribers.push(router.subscribe((route) => {
    if (destroyed) return;
    foldState.close();
    mountPage(route.pageKey);
  }));

  // Context change
  unsubscribers.push(contextManager.subscribe(() => {
    if (destroyed) return;
    ctxSwitcher.update(contextManager.get());
    remountCurrentPage();
  }));

  // Access refresh event
  const offAccess = /** @type {any} */ (eventBus).on?.('permissions.access.changed', () => {
    if (destroyed) return;
    if (!bootReady) {
      pendingAccessRefresh = true;
      return;
    }
    doAccessRefresh().catch(err => {
      /** @type {any} */ (logger).error?.('[app-shell] Access refresh failed, keeping current state', err);
    });
  });
  if (offAccess) unsubscribers.push(offAccess);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  function safeDestroy() {
    if (currentPageController) {
      currentPageController.abort();
      currentPageController = null;
    }
    if (currentPage) {
      try {
        currentPage.destroy();
      } catch (err) {
        /** @type {any} */ (logger).error?.('[app-shell] Page destroy error', err);
      }
      currentPage = null;
    }
  }

  /**
   * @param {HTMLElement} container
   */
  function showLoading(container) {
    container.innerHTML = '';
    container.appendChild(
      createElement('div', { class: 'app-shell-loading' }, ['Loading navigation\u2026']),
    );
  }

  // ---------------------------------------------------------------------------
  // Destroy
  // ---------------------------------------------------------------------------

  function destroy() {
    destroyed = true;

    // Bump all version counters to invalidate in-flight async
    mountVersion++;
    accessRefreshVersion++;
    bootVersion++;

    // Unsubscribe all
    for (const unsub of unsubscribers) unsub();
    unsubscribers.length = 0;

    // Abort boot controller (if in-flight)
    if (currentBootController) {
      currentBootController.abort();
      currentBootController = null;
    }

    // Abort access refresh controller (if in-flight)
    if (currentAccessRefreshController) {
      currentAccessRefreshController.abort();
      currentAccessRefreshController = null;
    }

    // Destroy current page
    safeDestroy();

    // Destroy sidebar
    if (sidebar) {
      sidebar.destroy();
      sidebar = null;
    }

    // Destroy user menu
    if (userMenu) {
      userMenu.destroy();
      userMenu = null;
    }

    // Destroy context switcher
    ctxSwitcher.destroy();

    // Destroy fold state
    foldState.destroy();

    // Destroy theme toggle
    if (themeToggle) {
      themeToggle.destroy();
      themeToggle = null;
    }

    // Destroy design toggle
    if (designToggle) {
      designToggle.destroy();
      designToggle = null;
    }

    // Remove resize listener
    window.removeEventListener('resize', onResize);
    clearTimeout(resizeTimer);

    // Remove DOM
    root.remove();
  }

  /**
   * Update brand display. Idempotent — safe to call multiple times.
   * No-op after destroy.
   * @param {{ appTitle?: string, logoUrl?: string }} config
   */
  function applyBrand(config) {
    if (destroyed) return;
    if (config.appTitle) {
      headerTitle.textContent = config.appTitle;
    }
    if (config.logoUrl) {
      if (headerLogo) {
        headerLogo.src = config.logoUrl;
      } else {
        headerLogo = /** @type {HTMLImageElement} */ (createElement('img', {
          class: 'app-shell-header-logo',
          src: config.logoUrl,
          alt: '',
        }));
        headerSlot.insertBefore(headerLogo, headerSlot.children[2] || null);
      }
      syncLogoClass();
    } else if (headerLogo) {
      headerLogo.remove();
      headerLogo = null;
      syncLogoClass();
    }
  }

  return { destroy, applyBrand };
}
